import React, { useEffect, useState } from 'react';
import './App.css';
import ParallaxHero from './components/ParallaxHero';
import './components/ParallaxHero.css';
import RunningScrollImage from './components/RunningScrollImage';
import Card from './components/Card';
import BusinessSolutionsSlider from './components/BusinessSolutionsSlider';
import ScrollZoomBackground from './components/ScrollZoomBackground';
import ZoomOutTextEffect from './components/ZoomOutTextEffect';
import DarkBlueCTA from './components/DarkBlueCTA';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import AboutCompany from './pages/AboutCompany';
import AboutTeam from './pages/AboutTeam';
import ComputerVision from './pages/ComputerVision';
import GenerativeAI from './pages/GenerativeAI';
import PredictiveAnalytics from './pages/PredictiveAnalytics';
import ContactUs from './pages/ContactUs';
import BlogsPage from './pages/BlogsPage';
import ArticlePage from './pages/ArticlePage';
import SolutionsPage from './pages/SolutionsPage';
import Navbar from './components/Navbar';
import { ParallaxProvider } from 'react-scroll-parallax';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Carousel from './components/Carousel';

function App() {
  // Load Vev design package
  const [imagePaths, setImagePaths] = useState({
    homepageBg: '/images/Homepage_2.jpg',
    computerVision: '/images/computer-vision.jpg',
    conversationalAi: '/images/conversational-ai.jpg',
    predictiveAnalytics: '/images/predictive-analytics.jpg',
    generativeBg: '/images/generative-bg.jpg',
    financeBg: '/images/finance-bg.jpg',
    healthcareBg: '/images/healthcare-bg.jpg',
    healthcareIndustry: '/images/healthcare_industry.jpg',
    financeIndustry: '/images/Finance_industry.jpg',
    manufacturingIndustry: '/images/Manufacturing_industry.jpg'
  });

  // State for dropdown menu
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  const toggleServicesDropdown = () => {
    setServicesDropdownOpen(!servicesDropdownOpen);
  };

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setServicesDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.vev.design/pkg/v1/f8aa0021a9e0d8871058775d27634152.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Hero section data
  const heroData = {
    title: 'HYPERION AI',
    subtitle: 'Revolutionize Your Business with Intelligent AI',
    description: 'Empower your organization with cutting-edge AI solutions that deliver real results. From predictive analytics to computer vision, we create tailored AI systems that drive efficiency, innovation, and competitive advantage in today\'s fast-paced market.',
    buttons: [
      { text: 'Explore Solutions', url: '/contact-us', primary: true },
      { text: 'View Our Work', url: '#services', primary: false }
    ],
    dynamicWords: [
      'Solutions',
      'Innovation',
      'Intelligence',
      'Transformation',
      'Excellence'
    ]
  };

  // Core business operations data
  const operationsData = [
    {
      id: 1,
      category: 'CUSTOMER INTELLIGENCE',
      title: "Customer Intelligence & Personalization",
      description: "Transform customer experiences with AI-powered analytics and personalization engines. Predict customer behavior, reduce churn by 45%, deliver hyper-personalized recommendations, and increase customer lifetime value through intelligent engagement strategies and real-time sentiment analysis.",
      image: '/images/customer-intelligence.jpg'
    },
    {
      id: 2,
      category: 'AUTOMATION',
      title: "Intelligent Process Automation",
      description: "Automate complex business processes by combining RPA with AI capabilities. Process documents with 95% accuracy, reduce manual tasks by 60%, enable intelligent decision-making, and streamline workflows across finance, HR, and operations with cognitive automation.",
      image: '/images/process-automation.jpg'
    },
    {
      id: 3,
      category: 'ASSET OPTIMIZATION',
      title: "Predictive Maintenance & Asset Intelligence",
      description: "Machine learning algorithms that predict equipment failures before they happen, reducing downtime by up to 78%. IoT sensor integration, real-time analytics, energy optimization, and sustainability tracking enable proactive maintenance scheduling and maximize asset performance.",
      image: '/images/predictive-maintenance.jpg'
    },
    {
      id: 4,
      category: 'SUPPLY CHAIN',
      title: "Supply Chain & Procurement Intelligence",
      description: "End-to-end supply chain optimization using advanced analytics and AI forecasting. Reduce logistics costs by 35%, optimize inventory levels, predict demand fluctuations, assess supplier risks, automate procurement, and enhance ESG compliance with intelligent supply chain visibility.",
      image: '/images/supply-chain.jpg'
    },
    {
      id: 5,
      category: 'FINANCIAL ANALYTICS',
      title: "Financial Analytics & Risk Management",
      description: "Advanced predictive financial modeling and real-time risk detection. Automate financial reporting, detect fraud with 99% accuracy, forecast revenue trends, optimize cash flow, identify anomalies, and ensure regulatory compliance with AI-powered financial intelligence platforms.",
      image: '/images/financial-analytics.jpg'
    }
  ];

  return (
    <ParallaxProvider>
      <Router>
        <div className="App">
          {/* Navigation */}
          <Navbar />

          <Routes>
            <Route path="/" element={
              <>
                {/* Hero Section with Parallax Effect using react-scroll-parallax */}
                <ParallaxHero 
                  title={heroData.title}
                  subtitle={heroData.subtitle}
                  description={heroData.description}
                  buttons={heroData.buttons}
                  dynamicWords={heroData.dynamicWords}
                />

                {/* Carousel Section */}
                <section className="carousel-section">
                  <div className="carousel-section-header">
                    <h2 className="section-title">Featured Insights</h2>
                    <p className="section-subtitle">Explore the latest in AI innovation and business transformation</p>
                  </div>
                  <Carousel />
                </section>

                {/* AI Services Section */}
                <section id="services" className="services-section">
                  <div className="services-header">
                    <h2 className="section-title">AI Services</h2>
                    <p className="section-subtitle">Explore our comprehensive suite of AI solutions</p>
                  </div>
                  
                  {/* Computer Vision */}
                  <div className="service-split-item">
                    <div className="service-split-container">
                      <div className="service-text-side">
                        <div className="service-content">
                          <span className="service-category">VISUAL AI</span>
                          <h3 className="service-title">Computer Vision & Image Recognition</h3>
                          <p className="service-description">Transform visual data into actionable insights with advanced computer vision. Deploy object detection, facial recognition, OCR, video analytics, and automated quality inspection systems with 99%+ accuracy for manufacturing, healthcare, and retail applications.</p>
                          <Link to="/computer-vision" className="service-button">
                            LEARN MORE →
                          </Link>
                        </div>
                      </div>
                      <div className="service-image-side">
                        <img src={imagePaths.computerVision} alt="Computer Vision" className="service-image" />
                      </div>
                    </div>
                  </div>

                  {/* Large Language Models & Generative AI */}
                  <div className="service-split-item">
                    <div className="service-split-container reverse">
                      <div className="service-text-side">
                        <div className="service-content">
                          <span className="service-category">GENERATIVE AI</span>
                          <h3 className="service-title">Large Language Models & Gen AI</h3>
                          <p className="service-description">Harness the power of GPT-4, Claude, and custom LLMs for intelligent automation. Build RAG systems, generate content, automate code generation, process documents, and create sophisticated conversational AI with enterprise-grade security and compliance.</p>
                          <Link to="/generative-ai" className="service-button">
                            LEARN MORE →
                          </Link>
                        </div>
                      </div>
                      <div className="service-image-side">
                        <img src={imagePaths.conversationalAi} alt="Generative AI" className="service-image" />
                      </div>
                    </div>
                  </div>

                  {/* Natural Language Processing */}
                  <div className="service-split-item">
                    <div className="service-split-container">
                      <div className="service-text-side">
                        <div className="service-content">
                          <span className="service-category">NLP & TEXT ANALYTICS</span>
                          <h3 className="service-title">Natural Language Processing</h3>
                          <p className="service-description">Extract insights from unstructured text with advanced NLP. Perform sentiment analysis, entity recognition, text classification, language translation, and semantic search. Process customer feedback, legal documents, and support tickets at scale with AI-powered understanding.</p>
                          <Link to="/solutions" className="service-button">
                            LEARN MORE →
                          </Link>
                        </div>
                      </div>
                      <div className="service-image-side">
                        <img src="/images/nlp-service.jpg" alt="Natural Language Processing" className="service-image" />
                      </div>
                    </div>
                  </div>

                  {/* Predictive Analytics */}
                  <div className="service-split-item">
                    <div className="service-split-container reverse">
                      <div className="service-text-side">
                        <div className="service-content">
                          <span className="service-category">MACHINE LEARNING</span>
                          <h3 className="service-title">Predictive Analytics & Forecasting</h3>
                          <p className="service-description">Turn data into competitive advantage with predictive analytics. Forecast demand, predict customer churn, optimize pricing, detect anomalies, and automate decision-making with advanced ML models, time series analysis, and real-time analytics dashboards.</p>
                          <Link to="/predictive-analytics" className="service-button">
                            LEARN MORE →
                          </Link>
                        </div>
                      </div>
                      <div className="service-image-side">
                        <img src={imagePaths.predictiveAnalytics} alt="Predictive Analytics" className="service-image" />
                      </div>
                    </div>
                  </div>

                  {/* Recommendation Systems */}
                  <div className="service-split-item">
                    <div className="service-split-container">
                      <div className="service-text-side">
                        <div className="service-content">
                          <span className="service-category">PERSONALIZATION</span>
                          <h3 className="service-title">Recommendation Systems & AI Personalization</h3>
                          <p className="service-description">Build sophisticated recommendation engines using collaborative filtering, deep learning, and hybrid approaches. Deliver personalized product recommendations, content suggestions, and dynamic user experiences that increase engagement by 40% and drive revenue growth.</p>
                          <Link to="/solutions" className="service-button">
                            LEARN MORE →
                          </Link>
                        </div>
                      </div>
                      <div className="service-image-side">
                        <img src="/images/recommendation-service.jpg" alt="Recommendation Systems" className="service-image" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Industry Solutions with Running Scroll Animation */}
                <section className="industry-section">
                  <div className="section-content">
                    <h2 className="section-title">Industry Solutions</h2>
                    <div className="industry-grid">
                      <Card className="industry-card" delay={0}>
                        <div className="industry-icon">
                          <img src={imagePaths.healthcareIndustry} alt="Healthcare Industry" className="industry-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <h3 className="industry-title">Healthcare</h3>
                        <p className="industry-description">Revolutionize patient care with AI-powered diagnostics, personalized treatment plans, and predictive health monitoring systems.</p>
                      </Card>

                      <Card className="industry-card" delay={100}>
                        <div className="industry-icon">
                          <img src={imagePaths.financeIndustry} alt="Finance Industry" className="industry-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <h3 className="industry-title">Finance</h3>
                        <p className="industry-description">Enhance financial security with intelligent fraud detection, optimize trading strategies, and deliver personalized banking experiences powered by AI.</p>
                      </Card>

                      <Card className="industry-card" delay={200}>
                        <div className="industry-icon">
                          <img src={imagePaths.manufacturingIndustry} alt="Manufacturing Industry" className="industry-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <h3 className="industry-title">Manufacturing</h3>
                        <p className="industry-description">Transform manufacturing with smart factories, predictive maintenance systems, automated quality control, and optimized supply chain management.</p>
                      </Card>
                    </div>
                  </div>
                </section>

                {/* Business Core Solutions Section */}
                <section className="business-solutions-section">
                  <div className="business-solutions-header">
                    <h2 className="section-title">Business Core Solutions</h2>
                    <p className="section-subtitle">Transforming core business operations with AI-powered solutions</p>
                  </div>
                  
                  {operationsData.map((operation, index) => (
                    <div key={operation.id} className="business-solution-item">
                      <div className={`business-solution-container ${index % 2 === 1 ? 'reverse' : ''}`}>
                        <div className="business-solution-text-side">
                          <div className="business-solution-content">
                            <span className="business-solution-category">{operation.category}</span>
                            <h3 className="business-solution-title">{operation.title}</h3>
                            <p className="business-solution-description">{operation.description}</p>
                            <Link to="/contact-us" className="business-solution-button">
                              LEARN MORE →
                            </Link>
                          </div>
                        </div>
                        <div className="business-solution-image-side">
                          <img 
                            src={operation.image} 
                            alt={operation.title}
                            className="business-solution-image"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/800x600/f3f4f6/1f2937?text=' + encodeURIComponent(operation.category);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </section>

                {/* Testimonials Section */}
                <TestimonialsSection />

                {/* FAQ Section */}
                <FAQSection />

                {/* CTA Section with Running Scroll Animation */}
                <DarkBlueCTA />

                {/* Footer */}
                <footer className="enhanced-footer">
                  <div className="footer-container">
                    <div className="footer-content">
                      <div className="footer-main">
                        <div className="footer-brand">
                          <h3 className="footer-logo">HYPERION AI</h3>
                          <p className="footer-description">
                            Transforming businesses worldwide with cutting-edge artificial intelligence solutions that drive real results and sustainable growth.
                          </p>
                          <div className="social-links">
                            <a href="#" className="social-link" aria-label="LinkedIn">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </a>
                            <a href="#" className="social-link" aria-label="Twitter">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                              </svg>
                            </a>
                            <a href="#" className="social-link" aria-label="GitHub">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                              </svg>
                            </a>
                          </div>
                        </div>

                        <div className="footer-links">
                          <div className="footer-column">
                            <h4 className="footer-heading">AI Services</h4>
                            <ul className="footer-list">
                              <li><Link to="/computer-vision">Computer Vision</Link></li>
                              <li><Link to="/generative-ai">Generative AI</Link></li>
                              <li><Link to="/predictive-analytics">Predictive Analytics</Link></li>
                              <li><a href="#">Automation & Robotics</a></li>
                            </ul>
                          </div>
                          <div className="footer-column">
                            <h4 className="footer-heading">Industries</h4>
                            <ul className="footer-list">
                              <li><a href="#">Healthcare</a></li>
                              <li><a href="#">Finance</a></li>
                              <li><a href="#">Manufacturing</a></li>
                              <li><a href="#">Retail</a></li>
                            </ul>
                          </div>
                          <div className="footer-column">
                            <h4 className="footer-heading">Company</h4>
                            <ul className="footer-list">
                              <li><Link to="/about-company">About Us</Link></li>
                              <li><Link to="/about-team">Our Team</Link></li>
                              <li><Link to="/blogs">Blog</Link></li>
                              <li><Link to="/contact-us">Contact</Link></li>
                            </ul>
                          </div>
                        </div>

                        <div className="footer-newsletter">
                          <h4 className="footer-heading">Stay Updated</h4>
                          <p className="newsletter-description">
                            Get the latest insights on AI trends and innovations delivered to your inbox.
                          </p>
                          <form className="newsletter-form">
                            <div className="input-group">
                              <input
                                type="email"
                                placeholder="Enter your email"
                                className="newsletter-input"
                                required
                              />
                              <button type="submit" className="newsletter-button">
                                Subscribe
                              </button>
                            </div>
                          </form>
                          <p className="newsletter-privacy">
                            We respect your privacy. Unsubscribe at any time.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="footer-bottom">
                      <div className="footer-bottom-content">
                        <p className="copyright">
                          &copy; 2025 Hyperion AI Solutions. All rights reserved.
                        </p>
                        <div className="footer-bottom-links">
                          <a href="#">Privacy Policy</a>
                          <a href="#">Terms of Service</a>
                          <a href="#">Cookie Policy</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </footer>
              </>
            } />
            <Route path="/about-company" element={<AboutCompany />} />
            <Route path="/about-team" element={<AboutTeam />} />
            <Route path="/computer-vision" element={<ComputerVision />} />
            <Route path="/generative-ai" element={<GenerativeAI />} />
            <Route path="/predictive-analytics" element={<PredictiveAnalytics />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blogs/:articleSlug" element={<ArticlePage />} />
          </Routes>
        </div>
      </Router>
    </ParallaxProvider>
  );
}

export default App;
